import { getPrimaryInstrumentFromMedia, MintStatusModel } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../../lib/stitches'
import ProfileRegistry from '../../../../stores/registry/ProfileRegistry'
import Avatar from '../../../core/Avatar'
import Button from '../../../core/Button/Button'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'
import { IconButton } from '../../../core/Components'
import FavoriteButton from '../../../core/FavoriteButton'
import { DotsVertical, PauseIcon, PlayIcon, PlusIcon } from '../../../core/icons'
import { MediaTypes, MediaTypesType, segmentTrackRemix } from '../../../core/Utils'
import { gray15 } from '../../../platform/color'
import AudioVisualizer from '../../../player/AudioVisualizer'
import { formatTime } from '../../../player/PlayerUtils'
import MintInProgressRow from './MintInProgressRow'
import Table from './Table'
import { Animated, Spinner, TableProps, useAsyncEffect, useRemix } from './utils'

interface SampleRowProps {
  sample: IArpMedia
  active?: boolean
  onClick?: () => void
  hideRowPlayControls?: boolean
  isClip?: boolean
}

const StyledVisualizerContainer = styled('div', {
  width: '100%',
  height: '36px',
  display: 'flex',
  opacity: 0.3,
  position: 'relative',
  overflow: 'hidden'
})

const DPI_SCALE = devicePixelRatio || 2
const VISUALIZER_OPTIONS = {
  color: '#000000'
}

const FAKE_URI = `https://d2hgqm22ttdu22.cloudfront.net/cc0-samples-wav2/013_Sabriel_Vocal_Pack/Vox/Sab_NoOnesHomie2.wav`

const VisualizerContainer = StyledVisualizerContainer

const segmentTrackButton = async (index: number, button: string) => {
  const sampleDetails = await ArpRegistry.getByIdAsync(index)
  let location = window.location.pathname.includes('explore')
    ? 'explore table'
    : window.location.pathname.includes('home')
    ? 'home table'
    : window.location.pathname.includes('profile')
    ? 'profile table'
    : 'unknown'
  window.analytics.track('Button Clicked', {
    button: button,
    token: index,
    source: window.location.pathname,
    sample: sampleDetails.metadata.title,
    location: location,
    type: 'sample',
    artistName: sampleDetails.user.displayName,
    artistEthAddress: sampleDetails.user.walletAddress,
    genres: sampleDetails.metadata.genres,
    bpm: sampleDetails.metadata.bpm,
    tags: sampleDetails.metadata.tags
  })
}

const SampleVisualizer: FC<{ playing?: boolean; sample: IArpMedia }> = observer(({ sample }) => {
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer>()
  useAsyncEffect(async () => {
    setAudioBuffer(await sample.fetchAudioBuffer())
  }, [sample])
  return (
    <VisualizerContainer id="visualizer">
      {audioBuffer && <AudioVisualizer audioBuffer={audioBuffer} options={VISUALIZER_OPTIONS} />}
    </VisualizerContainer>
  )
})
const StyledPlayIcon = styled(PlayIcon, {
  position: 'absolute',
  left: '16px'
})
const StyledPauseIcon = styled(PauseIcon, {
  position: 'absolute',
  left: '16px'
})
const Play = StyledPlayIcon
const Pause = StyledPauseIcon

export const SampleRow: FC<SampleRowProps> = observer(
  ({ sample, active, onClick, hideRowPlayControls, isClip }) => {
    const artist = ProfileRegistry.getByIndexReactive(sample.user.walletAddress)
    const artistAvatar = artist?.profile_picture
    const artistName = artist?.display_name
    const instrument = getPrimaryInstrumentFromMedia(sample) || '-'
    const [sampleDuration, setSampleDuration] = useState<number>()

    useAsyncEffect(async () => {
      // TODO: wait for real remix count here
      const sampleBuffer: AudioBuffer = await sample.fetchAudioBuffer()
      setSampleDuration(sampleBuffer.duration)
    }, [])

    const navigate = useNavigate()
    const handleMore = () => navigate('/sample?token=' + sample.id)

    let indicator = null
    const { remixSample, status } = useRemix()
    const [hover, setHover] = useState<boolean>(false)
    if (!hideRowPlayControls) {
      if (active) indicator = <Pause />
      else if (hover) indicator = <Play />
    }

    return (
      <Table.Row
        active={active}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onClick={() => {
          onClick()
          segmentTrackButton(sample.id, 'Table Entry')
        }}
      >
        {indicator}
        <Avatar imageUrl={artistAvatar} size={'26px'} />
        <Table.Col
          css={{
            marginLeft: '14px',
            flexGrow: 1,
            '@bigscreen': { minWidth: '160px', flexGrow: 'unset' }
          }}
        >
          <Table.Text bold size={'14'} css={{ color: gray15, whiteSpace: 'nowrap' }}>
            <Animated id={'animated'}>{sample.metadata.title}</Animated>
          </Table.Text>
          <Table.Text medium size={'14'} css={{ whiteSpace: 'nowrap' }} link>
            <Animated id={'animated'} css={{ '&:hover': { textDecoration: 'underline' } }}>
              <Link to={`/profile/${sample.user.walletAddress}`}>{artistName}</Link>
            </Animated>
          </Table.Text>
        </Table.Col>

        <Table.Col
          css={{ flex: 1, marginLeft: '32px', display: 'none', '@bigscreen': { display: 'flex' } }}
        >
          <SampleVisualizer sample={sample} />
        </Table.Col>
        <Table.Col
          css={{
            minWidth: '120px',
            marginLeft: '32px',
            display: 'none',
            '@tablet': { display: 'flex' }
          }}
        >
          <Table.Text>{instrument}</Table.Text>
        </Table.Col>

        <Table.Col
          css={{
            minWidth: '100px',
            marginLeft: '32px',
            display: 'none',
            '@tablet': { display: 'flex' }
          }}
        >
          <Table.Text>{sample.remixCount}</Table.Text>
        </Table.Col>

        <Table.Col
          css={{
            marginLeft: '32px',
            marginRight: '32px',
            display: 'flex',
            '@tablet': { display: 'none' }
          }}
        >
          <Table.Text>{formatTime(sampleDuration)}</Table.Text>
        </Table.Col>

        <Table.Col
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            '@tablet': {
              minWidth: '140px'
            }
          }}
        >
          <Button
            rounded
            type={'outlined'}
            css={{ paddingLeft: '8px', display: 'none', '@tablet': { display: 'flex' } }}
            onClick={(e) => {
              e.stopPropagation()
              segmentTrackRemix('sample', sample.id, 'details table')
              remixSample(sample.id, isClip)
            }}
          >
            {status === 'loading' ? (
              <Spinner style={{ marginRight: '4px' }} />
            ) : (
              <PlusIcon style={{ marginRight: '4px' }} />
            )}
            {'Studio'}
          </Button>
          <FavoriteButton
            arpMediaIndex={sample.id}
            mediaType={MediaTypes.SAMPLE as MediaTypesType}
          />
          <IconButton
            css={{
              opacity: hover ? 1 : 0.5,
              pointerEvents: !hover && 'none',
              display: 'none',
              '@bigscreen': { display: 'flex' }
            }}
            onClick={handleMore}
          >
            <DotsVertical />
          </IconButton>
        </Table.Col>
      </Table.Row>
    )
  }
)

const SampleTable: FC<
  Omit<TableProps, 'data'> & {
    data: (IArpMedia | MintStatusModel)[]
    onSelectMintInProgress?: (jobId: string) => void
    clipArpIndexes?: number[]
  }
> = ({
  data,
  onSelect,
  loading,
  selected,
  onDropdownSelect,
  onSelectMintInProgress,
  hideRowPlayControls,
  clipArpIndexes
}) => {
  return (
    <Table.Root>
      <Table.Header
        css={{
          display: 'none',
          '@tablet': { display: 'flex' }
        }}
      >
        <Table.Col css={{ flex: 1 }}>
          <Table.Text bold>{'Title'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '120px', marginLeft: '32px' }}>
          <Table.Text bold>{'Instrument'}</Table.Text>
        </Table.Col>
        <Table.Col
          css={{
            width: '100px',
            marginLeft: '32px'
          }}
        >
          <Table.Text bold>{'Times Used'}</Table.Text>
        </Table.Col>
        <Table.Col css={{ width: '140px' }} />
      </Table.Header>
      <Table.Body>
        {loading ? (
          <CenteredLoadingAnimation />
        ) : (
          data.map((sampleOrMintStatusEntity) => {
            if (!sampleOrMintStatusEntity) {
              return null
            }
            if ('jobId' in sampleOrMintStatusEntity) {
              return (
                <MintInProgressRow
                  key={sampleOrMintStatusEntity.jobId}
                  mintStatusEntity={sampleOrMintStatusEntity}
                  onClick={() => onSelectMintInProgress(sampleOrMintStatusEntity.jobId)}
                  songPreview={false}
                />
              )
            }

            const sample = sampleOrMintStatusEntity as IArpMedia
            return (
              <SampleRow
                sample={sampleOrMintStatusEntity}
                key={sampleOrMintStatusEntity.id}
                onClick={() => onSelect(sample.id)}
                active={selected === sample.id}
                hideRowPlayControls={hideRowPlayControls}
                isClip={clipArpIndexes && clipArpIndexes.includes(sample.id)}
              />
            )
          })
        )}
      </Table.Body>
    </Table.Root>
  )
}

export default observer(SampleTable)
